import { observer } from 'mobx-react-lite';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FaCheck, FaFileDownload, FaTimes } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { Header, Icon, Input, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import TableColumnPopover from '../../app/common/popover/TableColumnPopover';
import PaginatedTable from '../../app/common/table/PaginatedTable';
import DateHelper from '../../app/helpers/dateHelper';
import { formatLeaveDays } from '../../app/helpers/numberHelper';
import { OffsetRequest } from '../../app/models/offsetRequest';
import { useStore } from '../../app/stores/store';
import { history } from '../../index';

const dateHelper = new DateHelper();
const swal = withReactContent(Swal);
const StyledContainer = styled.div`padding: ${props => props.theme.padding.featureContainer};`;
const StyledHeader = styled(Header)`margin-bottom: 20px !important;`;
const StyledTableCard = styled.div`
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
`;
const StyledSearch = styled.div`
    display: flex;
    align-items: center;
    padding: 16px;
    border-bottom: 2px solid #d7e8f9;
`;
const StyledSearchIcon = styled(Icon)`
    font-size: 1.3em !important;
    color: #8d9eb4 !important;
`;
const StyledSearchInput = styled(Input)`
    flex: 1;
    min-width: 0;
    && input {
        border: none !important;
        padding: 10px;
    }
`;
const StyledClearButton = styled.button`
    border: none;
    background: transparent;
    color: #8d9eb4;
    padding: 10px;
    cursor: pointer;
    &:focus-visible { outline: 2px solid #3085d6; }
`;
const StyledAttachment = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;
const StyledDownloadButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    &:hover { background: #f3f9ff; }
    &:focus-visible { outline: 2px solid #3085d6; }
`;

const showDecisionError = (message: string) => {
    // SweetAlert treats validation messages as HTML; server errors must remain text.
    swal.showValidationMessage('Unable to complete the request.');
    const validationMessage = swal.getValidationMessage();
    if (validationMessage) validationMessage.textContent = message;
};

const OffsetRequestList = () => {
    const { offsetRequestStore } = useStore();
    const { offsetRequests, total, isLoading, isDeciding, error, filterOffsetRequests,
        approveOffsetRequest, rejectOffsetRequest, downloadAttachment } = offsetRequestStore;
    const session = offsetRequestStore.sessionVersion;
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const search = params.get('search') ?? '';
    const requestedPage = Number(params.get('page'));
    const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
    const [searchState, setSearchState] = useState({ session, value: search });
    const searchValue = searchState.session === session ? searchState.value : '';
    const searchTimer = useRef<number>();
    const previousSession = useRef<number | null>(null);
    const activeDialog = useRef<object | null>(null);

    useLayoutEffect(() => { offsetRequestStore.observeSession(); }, [offsetRequestStore]);

    useLayoutEffect(() => () => {
        clearTimeout(searchTimer.current);
        if (activeDialog.current !== null) {
            activeDialog.current = null;
            swal.close();
        }
    }, [session]);

    useEffect(() => {
        if (session === null) return;
        const changedSession = previousSession.current !== null && previousSession.current !== session;
        previousSession.current = session;
        setSearchState({ session, value: changedSession ? '' : search });
        clearTimeout(searchTimer.current);
        if (changedSession && (search || page !== 1)) {
            const currentParams = new URLSearchParams(location.search);
            currentParams.delete('search');
            currentParams.delete('page');
            history.replace({ search: currentParams.toString() });
            return;
        }
        filterOffsetRequests({ pageSize: 20, pageNumber: page, searchKeyword: search });
        return () => clearTimeout(searchTimer.current);
    }, [search, page, session, location.search, filterOffsetRequests]);

    const isCurrentView = () => session !== null && offsetRequestStore.sessionVersion === session;

    const handleSearch = (value: string) => {
        if (!isCurrentView()) return;
        setSearchState({ session, value });
        clearTimeout(searchTimer.current);
        searchTimer.current = window.setTimeout(() => {
            if (!isCurrentView()) return;
            params.set('search', value.trim());
            params.set('page', '1');
            history.push({ search: params.toString() });
        }, 500);
    };

    const handlePageChange = (pageNumber: number) => {
        if (!isCurrentView()) return;
        params.set('page', pageNumber.toString());
        history.push({ search: params.toString() });
    };

    const handleApprove = (request: OffsetRequest) => {
        if (!isCurrentView()) return;
        const dialog = {};
        activeDialog.current = dialog;
        const isCurrentDialog = () => isCurrentView() && activeDialog.current === dialog;
        swal.fire({
            title: 'Approval Confirmation',
            text: 'Are you sure you want to approve this offset credit request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Confirm',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !swal.isLoading(),
            allowEscapeKey: () => !swal.isLoading(),
            didClose: () => {
                if (activeDialog.current === dialog) activeDialog.current = null;
            },
            preConfirm: async () => {
                if (!isCurrentDialog()) return false;
                const approved = await approveOffsetRequest(request.id);
                if (!isCurrentDialog()) return false;
                if (!approved) {
                    showDecisionError(offsetRequestStore.decisionError || 'A decision is already in progress.');
                    return false;
                }
                return true;
            }
        });
    };

    const handleReject = (request: OffsetRequest) => {
        if (!isCurrentView()) return;
        const dialog = {};
        activeDialog.current = dialog;
        const isCurrentDialog = () => isCurrentView() && activeDialog.current === dialog;
        swal.fire({
            title: 'Rejection Confirmation',
            text: 'Are you sure you want to reject this offset credit request?',
            icon: 'question',
            input: 'textarea',
            inputLabel: 'Rejection reason',
            inputAttributes: { maxlength: '2000' },
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Confirm',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !swal.isLoading(),
            allowEscapeKey: () => !swal.isLoading(),
            didClose: () => {
                if (activeDialog.current === dialog) activeDialog.current = null;
            },
            preConfirm: async (value: string) => {
                if (!isCurrentDialog()) return false;
                if (!value.trim() || value.length > 2000) {
                    swal.showValidationMessage('A rejection reason of up to 2000 characters is required.');
                    return false;
                }
                const rejected = await rejectOffsetRequest(request.id, value.trim());
                if (!isCurrentDialog()) return false;
                if (!rejected) {
                    showDecisionError(offsetRequestStore.decisionError || 'A decision is already in progress.');
                    return false;
                }
                return true;
            }
        });
    };

    return (
        <StyledContainer>
            <StyledHeader>Offset Credit Request</StyledHeader>
            <StyledTableCard>
                <StyledSearch>
                    <StyledSearchIcon name='search' />
                    <StyledSearchInput aria-label='Search employees' placeholder='Search name...'
                        value={searchValue}
                        onChange={(_, data) => handleSearch(data.value)} />
                    {searchValue && <StyledClearButton type='button' aria-label='Clear search'
                        onClick={() => handleSearch('')}>
                        <Icon name='close' />
                    </StyledClearButton>}
                </StyledSearch>
                {session !== null && error && <Message negative role='alert' content={error} />}
                <PaginatedTable<OffsetRequest>
                    columns={[
                        { title: 'Employee', field: 'employee' },
                        { title: 'Department', field: 'department' },
                        { title: 'From', cellStyle: { width: '180px' }, render: request => dateHelper.formatDateTime(request.from) },
                        { title: 'To', cellStyle: { width: '180px' }, render: request => dateHelper.formatDateTime(request.to) },
                        { title: 'Duration (days)', render: request => formatLeaveDays(request.duration) },
                        { title: 'Reason', render: request => <TableColumnPopover width={150} content={request.reason} /> },
                        {
                            title: 'Attachment', render: request => request.file && (
                                <StyledAttachment>
                                    <StyledDownloadButton type='button' title={request.file.name}
                                        aria-label={`Download ${request.file.name}`}
                                        onClick={() => {
                                            if (isCurrentView()) downloadAttachment(request.id, request.file!.name);
                                        }}>
                                        <FaFileDownload style={{ color: '#21ba45', fontSize: '16px' }} />
                                    </StyledDownloadButton>
                                    <TableColumnPopover width={150} content={request.file.name} />
                                </StyledAttachment>
                            )
                        },
                        { title: 'Status', cellStyle: { width: '70px' }, render: request => request.rejectReason
                            ? <TableColumnPopover columnContent={request.status} content={request.rejectReason} /> : request.status },
                        { title: 'Date Filed', cellStyle: { width: '100px' }, render: request => dateHelper.formatShortDate(request.dateFiled) },
                        { title: 'Date Approved', cellStyle: { width: '180px' }, render: request => dateHelper.formatDateTime(request.dateApproved ?? '') },
                        { title: 'Approved By', render: request => request.status === 'Approved' ? request.approvedBy : '' }
                    ]}
                    data={session === null ? [] : offsetRequests}
                    total={session === null ? 0 : total}
                    isLoading={session !== null && isLoading}
                    pageSize={20}
                    pageNumber={page}
                    onChangePage={handlePageChange}
                    sorting={false}
                    draggable={false}
                    toolbar={false}
                    search={false}
                    style={{ borderRadius: '0 0 16px 16px' }}
                    actions={[
                        request => ({
                            icon: () => <FaCheck style={{ color: '#97a4b5', fontSize: '16px' }} />,
                            tooltip: 'Approve',
                            onClick: () => handleApprove(request),
                            disabled: session === null || isLoading || isDeciding,
                            hidden: request.status !== 'Pending'
                        }),
                        request => ({
                            icon: () => <FaTimes style={{ color: '#97a4b5', fontSize: '16px' }} />,
                            tooltip: 'Reject',
                            onClick: () => handleReject(request),
                            disabled: session === null || isLoading || isDeciding,
                            hidden: request.status !== 'Pending'
                        })
                    ]}
                />
            </StyledTableCard>
        </StyledContainer>
    );
};

export default observer(OffsetRequestList);
